import React, { useMemo, useState } from "react";
import { Table } from "antd";
import CcButton from "../../CoilConfigurator/components/CcButton";
import OpStatusPill from "../components/OpStatusPill";
import type { ConfigurationResult, EquipmentKind } from "../models/types";

interface BuildSpecsPanelProps {
  rows: ConfigurationResult[];
  loading: boolean;
  onOpenBuildSpec: (configurationId: string, equipment: EquipmentKind) => void;
}

const BuildSpecsPanel = ({ rows, loading, onOpenBuildSpec }: BuildSpecsPanelProps) => {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (row) =>
        row.project.projectNumber.toLowerCase().includes(q) ||
        row.project.projectName.toLowerCase().includes(q) ||
        row.configurationId.toLowerCase().includes(q),
    );
  }, [query, rows]);

  return (
    <div className="op-build-specs-panel">
      <div className="op-toolbar">
        <input
          className="op-search"
          type="search"
          placeholder="Search by project or configuration ID"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          aria-label="Search build specifications"
        />
      </div>

      <h2 className="op-section-title">Build Specifications</h2>
      <p className="op-muted op-build-specs-panel__intro">
        Open a manufacturing build specification for Blast Frame or Induction Coil from a saved
        configuration.
      </p>

      <Table
        className="op-table"
        loading={loading}
        rowKey="configurationId"
        dataSource={filtered}
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
        locale={{ emptyText: "No configurations yet. Generate one to create build specifications." }}
        columns={[
          { title: "Project", dataIndex: ["project", "projectNumber"], key: "project" },
          { title: "Configuration", dataIndex: "configurationId", key: "configurationId" },
          {
            title: "Status",
            key: "status",
            render: (_: unknown, row: ConfigurationResult) => <OpStatusPill status={row.status} />,
          },
          { title: "Revision", dataIndex: "revision", key: "revision" },
          {
            title: "Blast Frame",
            key: "blastFrame",
            render: (_: unknown, row: ConfigurationResult) =>
              row.blastFrame ? (
                <CcButton
                  variant="link"
                  onClick={() => onOpenBuildSpec(row.configurationId, "blast-frame")}
                >
                  Open build spec
                </CcButton>
              ) : (
                <span className="op-muted">—</span>
              ),
          },
          {
            title: "Induction Coil",
            key: "inductionCoil",
            render: (_: unknown, row: ConfigurationResult) =>
              row.inductionCoil ? (
                <CcButton
                  variant="link"
                  onClick={() => onOpenBuildSpec(row.configurationId, "induction-coil")}
                >
                  Open build spec
                </CcButton>
              ) : (
                <span className="op-muted">—</span>
              ),
          },
        ]}
      />
    </div>
  );
};

export default BuildSpecsPanel;
